import { useEffect, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { trackTour } from "@/components/tour/PlatformTour";

/** The tour as a plain video file.
 *
 *  Used when the app is running as a home-screen install. An installed PWA on iOS gets a far
 *  smaller memory ceiling than Safari, and the interactive tour — a React animation engine
 *  decoding a dozen full-size UI screenshots — crossed it about thirty seconds in: iOS killed the
 *  web view and the service worker relaunched at the start URL, which read as the film "going
 *  white and dropping back to the home page". The rendered MP4 carries the same film with the
 *  captions already in the picture, and video decoding is handled natively, so it cannot blow the
 *  limit. Browsers and desktop keep the interactive player.
 */
export default function TourVideo({ onClose, onStart }) {
  const ref = useRef(null);
  const fired = useRef(new Set());
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  // Our API streams the film with a video content type and byte ranges. If that ever fails, fall
  // back to the file on the static host rather than showing the viewer a dead player.
  // Narrow screens get the 720p cut so it starts immediately on cellular; anything wider gets the
  // full-quality master.
  const [src, setSrc] = useState(() => {
    const phone = typeof window !== "undefined"
      && (window.innerWidth < 1024 || window.matchMedia?.("(pointer: coarse)").matches);
    return `${API}/public/tour/${phone ? "phone" : "wide"}.mp4`;
  });

  // Version the URL with the fingerprint of the file on the server, so a cut cached by a CDN,
  // a service worker or the browser from an earlier render can never be played instead.
  useEffect(() => {
    let live = true;
    fetch(`${API}/public/tour/manifest`).then((r) => r.json()).then((m) => {
      if (!live) return;
      setSrc((current) => {
        const cut = current.includes("/phone.mp4") ? "phone" : "wide";
        const v = m?.cuts?.[cut]?.v;
        return v && current.startsWith(API) ? `${API}/public/tour/${cut}.mp4?v=${v}` : current;
      });
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  useEffect(() => {
    trackTour("loaded");
    const el = ref.current;
    if (!el) return;
    const onTime = () => {
      if (!el.duration) return;
      const pct = el.currentTime / el.duration;
      [[0.25, "progress_25"], [0.5, "progress_50"], [0.75, "progress_75"]].forEach(([at, ev]) => {
        if (pct >= at && !fired.current.has(ev)) {
          fired.current.add(ev);
          trackTour(ev, el.currentTime);
        }
      });
    };
    const onEnd = () => {
      if (!fired.current.has("completed")) {
        fired.current.add("completed");
        trackTour("completed", 100);
      }
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
    };
  }, []);

  return (
    <div data-testid="tour-video-player"
         className="fixed inset-0 z-50 flex flex-col bg-black" role="dialog"
         style={{ height: "100dvh" }} aria-label="Cornerstone Ops platform tour">
      <div className="relative flex-1 min-h-0 flex items-center justify-center">
        {!ready && !failed && (
          <Loader2 data-testid="tour-video-loading" className="h-6 w-6 animate-spin text-gold" />
        )}
        <video ref={ref} data-testid="tour-video"
               className="h-full w-full object-contain"
               src={src}
               poster="/tour/poster.jpg"
               controls playsInline preload="metadata"
               onCanPlay={() => setReady(true)}
               onPlay={() => {
                 if (!fired.current.has("started")) {
                   fired.current.add("started");
                   trackTour("started", 0);
                 }
               }}
               onError={() => {
                 const el = ref.current;
                 // One retry on the static copy before telling the viewer it cannot play: a
                 // transient decode error while the first bytes arrive is not a failure.
                 if (src.startsWith(API)) {
                   setSrc("/tour/cornerstone-ops-tour-720.mp4");
                   return;
                 }
                 setTimeout(() => {
                   if (!el || el.readyState < 2) setFailed(true);
                 }, 2500);
               }} />
        {failed && (
          <div data-testid="tour-video-fallback"
               className="absolute inset-0 flex flex-col items-center justify-center bg-royal-deep/95
                          px-8 text-center">
            <p className="font-display text-lg font-semibold text-white">
              The film can’t play on this connection
            </p>
            <button data-testid="tour-video-fallback-start" onClick={onStart}
              className="mt-6 h-11 rounded-full bg-gold px-7 font-display text-xs font-bold
                         uppercase tracking-[0.16em] text-royal-deep">
              Start With Cornerstone
            </button>
          </div>
        )}
        <button data-testid="tour-video-close" onClick={onClose} aria-label="Close tour"
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center
                     rounded-full bg-royal-deep/75 text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="shrink-0 border-t border-white/10 bg-[#0b1330] px-4 py-3 text-center"
           style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <button data-testid="tour-video-cta" onClick={onStart}
          className="h-11 rounded-full bg-gold px-8 font-display text-xs font-bold uppercase
                     tracking-[0.16em] text-royal-deep">
          Start Free Trial
        </button>
      </div>
    </div>
  );
}
